import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "@mui/icons-material";
import { getDivisionStandingsAll, getUserPredictionsStrict } from "../../services/MatchService";
import { getAccountBySteamId } from "../../services/FantasyService";
import { currentSeason } from "../../Constants";
import { scorePrediction } from "../../utils/predictionScore";
import { getSimpleDivisionLabel } from "../../utils/divisionUtils";
import PredictionCards from "./predict/PredictionCards";
import {
  FIRST_PREDICTION_SEASON,
  PredictionsByDivision,
  StandingsByDivision,
  divisionLabel,
  majorOf,
  predictableDivisions,
  toPredictionMap,
} from "./predict/predictShared";
import styles from "./Predict.module.css";

const SEASONS = Array.from(
  { length: currentSeason - FIRST_PREDICTION_SEASON + 1 },
  (_, i) => currentSeason - i
);

/**
 * /standings/predict/:steamId — someone's predictions, read-only, as the same cards as your own
 * "Alle" view. ?season= picks the season; ?division= narrows to one division or a whole major
 * (the leaderboard links with either, e.g. division=2div).
 */
const UserPredictionsPage: React.FC = () => {
  const { t } = useTranslation(["pages", "common"]);
  const { steamId = "" } = useParams<{ steamId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const seasonParam = Number(searchParams.get("season"));
  const season = SEASONS.includes(seasonParam) ? seasonParam : currentSeason;
  const focus = searchParams.get("division") ?? "";

  const [playerName, setPlayerName] = useState("");
  const [data, setData] = useState<{ season: number; standings: StandingsByDivision; predictions: PredictionsByDivision } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAccountBySteamId(steamId)
      .then((account) => {
        if (!cancelled) setPlayerName(account?.playerName ?? "");
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [steamId]);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    Promise.all([getDivisionStandingsAll(season), getUserPredictionsStrict(steamId, season)])
      .then(([standings, predictions]) => {
        if (cancelled) return;
        setData({ season, standings: standings ?? {}, predictions: toPredictionMap(predictions) });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [steamId, season]);

  const current = data?.season === season ? data : null;
  const allDivisions = useMemo(() => predictableDivisions(current?.standings ?? null), [current]);
  const focusIsDivision = allDivisions.includes(focus);
  const divisions = focus
    ? allDivisions.filter((d) => (focusIsDivision ? d === focus : majorOf(d) === focus))
    : allDivisions;

  const name = playerName || steamId;
  const predictions = current?.predictions ?? {};
  const predicted = allDivisions.filter((d) => predictions[d]);
  const scores = predicted
    .map((d) => scorePrediction(predictions[d].teams, current?.standings[d]))
    .filter((s): s is NonNullable<typeof s> => s !== null);
  let summary = t("predict.summary", { done: predicted.length, total: allDivisions.length });
  if (scores.length) {
    const average = scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
    const exact = scores.reduce((sum, s) => sum + s.exact, 0);
    summary += ` · ${t("predict.summaryScore", { score: average.toFixed(2) })} · ${t("predict.summaryExact", { count: exact })}`;
  }

  const setParams = (next: { season?: number; division?: string }) => {
    const s = next.season ?? season;
    const d = next.division ?? focus;
    const params: Record<string, string> = {};
    if (s !== currentSeason) params.season = String(s);
    if (d) params.division = d;
    setSearchParams(params);
  };

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <Link
            className={styles.backLink}
            to={season === currentSeason ? "/standings/leaderboard" : `/standings/leaderboard?season=${season}`}
          >
            <ChevronLeft sx={{ fontSize: 18 }} />
            {t("predictLeaderboard.title")}
          </Link>
          <h1 className={styles.title}>{t("predict.userTitle", { name })}</h1>
          {current && (
            <div className={styles.statusLine}>
              <span>{summary}</span>
            </div>
          )}
        </div>
        <div className={styles.headRight}>
          <div role="group" aria-label={t("predict.seasonAria")} className={styles.pillGroup}>
            {SEASONS.map((s) => (
              <button
                key={s}
                type="button"
                className={`${styles.pill} ${s === season ? styles.pillActive : ""}`}
                aria-pressed={s === season}
                onClick={() => s !== season && setParams({ season: s })}
              >
                {s === season ? t("common:seasonNum", { number: s }) : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {focus && (
        <div className={styles.controls}>
          <div role="group" aria-label={t("predict.divisionAria")} className={styles.pillGroup}>
            <button type="button" className={styles.pill} aria-pressed={false} onClick={() => setParams({ division: "" })}>
              {t("predict.all")}
            </button>
            <button type="button" className={`${styles.pill} ${styles.pillActive}`} aria-pressed>
              {focusIsDivision ? divisionLabel(focus) : getSimpleDivisionLabel(focus)}
            </button>
          </div>
        </div>
      )}

      {failed ? (
        <div className={`${styles.note} ${styles.noteError}`}>{t("predict.loadError")}</div>
      ) : !current ? (
        <div className={styles.loadingText}>{t("predict.loading")}</div>
      ) : predicted.length === 0 ? (
        <div className={`${styles.panel} ${styles.signInPanel}`}>
          <span className={styles.cardEmptyText}>{t("predict.userEmpty", { name, season })}</span>
        </div>
      ) : (
        <PredictionCards
          season={season}
          divisions={divisions}
          standings={current.standings}
          predictions={predictions}
          owner="other"
          locked
        />
      )}
    </div>
  );
};

export default UserPredictionsPage;
